/**
 * One-time alias mapping step for OCR imports.
 *
 * Shows every account name the OCR found in the image alongside the best
 * match from the alias table / fuzzy heuristic. The user confirms or
 * corrects each mapping once; aliases are saved so future images auto-map.
 *
 * On confirm, calls onConfirmed with:
 *   - allowedNames  — Set of rawNames the user chose to import (skipped names absent)
 *   - workbookData  — The ParsedWorkbookResponse to pass to ImportDialog
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { OcrClassifyResponse, ParsedWorkbookResponse } from '../api';
import { Button, Card, ErrorBanner, Spinner } from '../ui/primitives';
import { Modal } from '../ui/Modal';

interface Props {
  data: OcrClassifyResponse;
  onConfirmed: (allowedNames: Set<string>, workbookData: ParsedWorkbookResponse) => void;
  onCancel: () => void;
}

interface RowMapping {
  rawName: string;
  /** null = skip this row */
  accountId: number | null;
  /** true when already saved in aliases table — no POST needed */
  alreadySaved: boolean;
}

export function OcrAliasStep({ data, onConfirmed, onCancel }: Props) {
  const { accountMatches, knownAccounts, sheets } = data;

  const [rows, setRows] = useState<RowMapping[]>(() =>
    accountMatches.map((m) => ({
      rawName: m.rawName,
      accountId: m.accountId,
      alreadySaved: m.via === 'alias',
    })),
  );
  const [err, setErr] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const toSave = rows.filter((r) => r.accountId !== null && !r.alreadySaved);
      for (const row of toSave) {
        await api.post(`/api/accounts/${row.accountId}/aliases`, {
          rawName: row.rawName,
        });
      }
    },
    onSuccess: () => {
      const allowedNames = new Set(
        rows.filter((r) => r.accountId !== null).map((r) => r.rawName),
      );
      onConfirmed(allowedNames, { sheets });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const mappedCount = rows.filter((r) => r.accountId !== null).length;

  function setAccountId(rawName: string, accountId: number | null) {
    setRows((prev) =>
      prev.map((r) =>
        r.rawName === rawName
          ? { ...r, accountId, alreadySaved: false }
          : r,
      ),
    );
  }

  return (
    <Modal onClose={onCancel} size="lg">
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <div>
            <h2 className="text-small font-semibold text-ink">Map upline accounts</h2>
            <p className="mt-0.5 text-caption text-ink-muted">
              Match each name from the image to an account. Saved once — auto-mapped forever.
            </p>
          </div>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </header>

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
          {accountMatches.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-small text-amber-700">
              <p className="font-semibold">No account names detected in the image.</p>
              <p className="mt-1 text-caption text-amber-600">
                This usually means Excel columns are too narrow (showing ####). Widen the columns
                in Excel so numbers are visible, then re-take the screenshot.
              </p>
            </div>
          ) : (
            <Card>
              <table className="min-w-full text-caption">
                <thead className="text-left text-ink-muted">
                  <tr>
                    <th className="px-2 py-1.5">Upline name (from image)</th>
                    <th className="px-2 py-1.5 pl-4">Maps to account</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((row) => {
                    const original = accountMatches.find((m) => m.rawName === row.rawName);
                    return (
                      <tr key={row.rawName}>
                        <td className="px-2 py-2 align-middle">
                          <span className="font-mono text-[11px] text-ink">
                            {row.rawName}
                          </span>
                          {original?.via === 'alias' && (
                            <span className="ml-2 rounded bg-pos/10 px-1 py-0.5 text-[10px] text-pos">
                              saved
                            </span>
                          )}
                          {original?.via === 'fuzzy' && (
                            <span className="ml-2 rounded bg-amber-100 px-1 py-0.5 text-[10px] text-amber-600">
                              suggested
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 pl-4 align-middle">
                          <select
                            className="w-full rounded-md border border-border bg-surface px-2 py-1 text-caption text-ink focus:border-brand focus:outline-none"
                            value={row.accountId ?? ''}
                            onChange={(e) =>
                              setAccountId(
                                row.rawName,
                                e.target.value === '' ? null : Number(e.target.value),
                              )
                            }
                          >
                            <option value="">— skip this row —</option>
                            {knownAccounts.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.label}
                                {a.panel ? ` (${a.panel})` : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}

          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>

        <footer className="flex items-center justify-between border-t border-border bg-surface-raised px-4 py-3">
          <span className="text-caption text-ink-muted">
            {mappedCount} of {accountMatches.length} account(s) will be imported
          </span>
          <Button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || mappedCount === 0}
          >
            {saveMut.isPending ? (
              <Spinner />
            ) : (
              `Continue with ${mappedCount} account(s) →`
            )}
          </Button>
        </footer>
    </Modal>
  );
}
